import styled from "styled-components";
import { useNewsProviderData } from "../../hooks/useNewsProviderData";
import {
  getNewsFeedUrl,
  INewsItem,
  NewsFeedLink,
} from "../../utils/contentFeeds";
import { ArrowEnd } from "../icons/Arrows";
import SubHeading from "../SubHeading";

const StyledArrowEnd = styled(ArrowEnd)`
  &:dir(rtl),
  [dir="rtl"] & {
    transform: rotate(180deg);
  }
`;

const Section = styled.section`
  margin: ${({ theme }) => `${theme.space[2]}`};
  color: ${({ theme }) => theme.colors.offBlack};

  @media (min-width: ${({ theme }) => theme.breakpoints[3]}) {
    margin: ${({ theme }) => `${theme.space[4]} 0`};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: ${({ theme }) => theme.space[3]} 0;
`;

const MoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledSubheading = styled(SubHeading)`
  margin: 0;
`;

const ArticleRail = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[3]};
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
`;

const Article = styled.a`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[3]};
  flex: 0 1 auto;
  min-width: 296px;
  border-radius: ${({ theme }) => theme.radii[1]};
  color: ${({ theme }) => theme.colors.offBlack};
  text-decoration: none;
  white-space: normal;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const ArticleImg = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16/9;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radii[1]};
`;

const ArticleTitle = styled.h4`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSizes[3]};
`;

const ArticleExcerpt = styled.p`
  margin: 0;
`;

interface IProps {
  numberOfArticles: number;
}

const NbaNewsProvider = ({ numberOfArticles }: IProps) => {
  const url = getNewsFeedUrl(numberOfArticles);
  const { newsItems } = useNewsProviderData({ url, numberOfArticles });

  if (!newsItems || newsItems.length === 0) {
    return null;
  }

  // Setting the language and text direction for the section
  // to EN as the content is rendered in English in all regions.
  return (
    <Section lang="en" dir="ltr">
      <Header>
        <StyledSubheading>Latest Fantasy News</StyledSubheading>
        <MoreLink href={NewsFeedLink} target="_blank" rel="noopener noreferrer">
          More
          <StyledArrowEnd />
        </MoreLink>
      </Header>
      <ArticleRail>
        {newsItems.map((item: INewsItem) => (
          <Article
            key={item.id}
            href={item.permalink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.featuredImage && (
              <ArticleImg src={item.featuredImage} alt="" />
            )}
            <ArticleTitle>{item.title}</ArticleTitle>
            {item.excerpt && <ArticleExcerpt>{item.excerpt}</ArticleExcerpt>}
          </Article>
        ))}
      </ArticleRail>
    </Section>
  );
};

export default NbaNewsProvider;
